import { Injectable } from '@angular/core';
import { ContentType, NotebookCellFacade, NotebookTypes } from '@mode/notebooks/contract';
import { Store } from '@ngrx/store';
import * as NotebookCellActions from './notebook-cell.actions';
import * as NotebookActions from '../notebook/notebook.actions';
import * as NotebookCellSelectors from '../notebook-cell/legacy-notebook-cell.selectors';
import * as NotebookSelector from '../notebook/legacy-notebook.selectors';
import { OrderedSet } from 'immutable';
import { BehaviorSubject, combineLatest, first, map, Observable, take } from 'rxjs';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConfirmDialogService } from '@mode/shared/ui';
import { FeatureFlagsFacade, FeatureFlag } from '@mode/shared/contract-common';

@Injectable()
export class NotebookCellService implements NotebookCellFacade {
  selectedNotebookToken$ = this.store.select(NotebookSelector.selectedNotebookToken);
  vizTokensArray$ = this.store.select(NotebookCellSelectors.vizTokensArray);
  selectedCellFrozen$ = this.store.select(NotebookCellSelectors.selectedCellFrozen);
  selectedNextCellToken = NotebookCellSelectors.selectedNextCellToken;
  isNotebookExportDatasetsEnabled$: Observable<boolean>;
  isNotebookDatasetsPublishedEnabled$: Observable<boolean>;

  nextCellToken$: Observable<string> | undefined;
  prevCellToken$: Observable<string> | undefined;

  constructor(
    private store: Store,
    private confirmDialogService: ConfirmDialogService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.isNotebookExportDatasetsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookExportDatasets);
    this.isNotebookDatasetsPublishedEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.NotebookPublishExportedDatasets
    );
  }

  updateContentType(cellToken: string, contentType: ContentType): void {
    this.store.dispatch(NotebookCellActions.UpdateContentType({ contentType, cellToken }));
  }

  editCellContent(content: string, cellToken: string): void {
    this.store.dispatch(NotebookCellActions.editCellContent({ content, cellToken }));
  }
  beginEditCell(cellToken: string): void {
    this.selectedCellFrozen$.subscribe((cellFrozen) => {
      this.store.dispatch(NotebookCellActions.beginEditCell({ cellToken, cellFrozen }));
    });
  }
  updateCell(cellToken: string): void {
    this.store.dispatch(NotebookCellActions.updateCell({ cellToken }));
  }
  selectCell(cellToken: string, resultPane: boolean): void {
    this.store.dispatch(NotebookCellActions.selectCell({ cellToken, resultPane }));
  }
  addCell({
    notebookToken,
    content,
    contentType,
    nextToken,
  }: {
    notebookToken: string;
    content?: string;
    contentType?: ContentType;
    nextToken?: string;
  }): void {
    this.store.dispatch(NotebookCellActions.addCell({ notebookToken, content, contentType, nextToken }));
  }
  moveCell(cellToken: string, down: boolean): void {
    this.selectedNotebookToken$.pipe(first()).subscribe((notebookToken: any) => {
      this.store.dispatch(NotebookCellActions.moveCell({ cellToken, notebookToken, down }));
    });
  }
  addCellToLayout(cellToken: string): void {
    this.store.dispatch(NotebookCellActions.addCellToLayout({ cellToken }));
  }
  foldCell(cellToken: string): void {
    this.store.dispatch(NotebookCellActions.foldCell({ cellToken }));
  }
  toggleFreezeCell(cellToken: string): void {
    this.store.dispatch(NotebookCellActions.toggleFreezeCell({ cellToken }));
  }
  removeCellFromLayout(cellToken: string): void {
    this.vizTokensArray$.subscribe((vizTokensArray) => {
      this.store.dispatch(
        NotebookCellActions.removeCellFromLayout({ cellToken, vizToken: vizTokensArray.get(cellToken) })
      );
    });
  }
  destroyCell(
    cellToken: string,
    nextCellToken: string,
    notebookToken: string,
    notebookCellTokensArray: Map<string, OrderedSet<string>>
  ): void {
    this.store.dispatch(
      NotebookCellActions.destroyCell({
        cellToken,
        nextCellToken,
        notebookToken,
        notebookCellTokensArray,
      })
    );
  }

  runCell(notebookToken: string, cellToken: string): void {
    this.store
      .select(NotebookCellSelectors.createCellContentTypeSelector(cellToken))
      .pipe(first())
      .subscribe((contentType) => {
        if (contentType === NotebookTypes.ContentType.Python) {
          this.store.dispatch(
            NotebookActions.run({
              notebookToken,
              runAllCells: false,
              cellTokensToRun: [cellToken],
            })
          );
        } else {
          this.store.dispatch(
            NotebookCellActions.updateCell({
              cellToken,
            })
          );
        }
      });
  }

  public removeCell(notebookToken: string, cellToken: string): void {
    combineLatest([
      this.isNotebookExportDatasetsEnabled$,
      this.store.select((state) => NotebookCellSelectors.isExportedByCell(state, cellToken)),
      this.store.select(NotebookCellSelectors.createCellExecutionCountSelector(cellToken)),
      this.store.select(NotebookCellSelectors.isPublishedDatasetCell, cellToken),
      this.isNotebookDatasetsPublishedEnabled$,
    ])
      .pipe(first())
      .subscribe(([exportEnabled, isExported, executionCount, isPublishedDataset, publishedEnabled]) => {
        const getDialogText = (): string => {
          if (exportEnabled) {
            if (isExported) {
              return `
                <p>Deleting <b class="bold-text">cell ${executionCount}</b> is irreversible and will permanently break content in this Report${
                  publishedEnabled && isPublishedDataset ? ` and the data workspace` : ``
                }.</p>
                <p class='bold-text unset-margin'>The following will break:</p>
                <ul class='unset-margin'>
                  <li>the local dataset created from this output cell</li>
                  <li>any calculated fields that were added to the dataset</li>
                  <li>any associated charts</li>
                  <li>any dependent Explorations</li>
                  ${
                    publishedEnabled && isPublishedDataset
                      ? `<li>the published table imported in the data workspace</li>
                         <li>any dependent Models or Worksheets</li>
                         <li>any dependent Reports or Liveboards</li>
                         <li>any dependent Answers</li>`
                      : ``
                  }
                </ul>`;
            }
            return `<p>Deleting <b class="bold-text">cell ${executionCount}</b> may impact other cells in the Notebook, and can’t be undone.</p>`;
          }
          return 'This may impact other cells in the Notebook and cannot be undone.';
        };

        this.confirmDialogService.open({
          dialogTitle: exportEnabled ? `Delete cell?` : `Delete cell ${executionCount}?`,
          dialogText: getDialogText(),
          confirmText: 'Delete',
          acknowledgeText:
            isPublishedDataset && publishedEnabled
              ? `I understand this will break content in this Report and the data workspace`
              : '',
          confirmAction: () => {
            combineLatest([
              this.store.select(NotebookSelector.notebookCellTokensArray),
              this.store.select(NotebookCellSelectors.selectedNextCellToken),
            ])
              .pipe(first())
              .subscribe(([notebookCellTokensArray, nextCellToken]) => {
                this.store.dispatch(
                  NotebookCellActions.destroyCell({
                    cellToken,
                    notebookToken,
                    nextCellToken: nextCellToken as string,
                    notebookCellTokensArray,
                  })
                );
              });
          },
          confirmButtonColor: 'alert',
          confirmButtonType: 'primary',
        });
      });
  }

  toggleFoldCell(cellToken: string): void {
    this.store.dispatch(NotebookCellActions.foldCell({ cellToken }));
  }
}
